import React, {
  createContext,
  useLayoutEffect,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
  ComponentType,
  ReactNode,
} from 'react'
import decode from 'jwt-decode'
import { fromUnixTime, isBefore } from 'date-fns'

export type TokenPayload = {
  id: number
  email: string
  fullName: string
  image: string
  exp: number
  impersonator?: string
}

type AuthState = {
  user: TokenPayload | undefined
  token?: string
  refreshToken?: string
  /** Updates state from server-set cookies */
  updateFromCookies: () => void
  logout: typeof logout
  login: typeof login
}

type Tokens = {
  token?: string
  refreshToken?: string
  user?: TokenPayload
}

// Cookies are now managed entirely server-side with httpOnly: true
// Client only reads cookies for state management

export const UserContext = createContext<AuthState>(null as any)
export const useUserContext = () => useContext(UserContext)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<Tokens>({
    token: undefined,
    refreshToken: undefined,
  })

  // hydrate from server once on mount
  useEffect(() => {
    getAuthStatus().then(setState)
  }, [])

  const user = useMemo<TokenPayload | undefined>(
    () =>
      state.user ||
      (state.token && state.token !== 'server-managed'
        ? decode(state.token)
        : undefined),
    [state.token, state.user],
  )

  const updateFromCookies = useCallback(async () => {
    const authStatus = await getAuthStatus()
    setState(authStatus)
  }, [])

  const value = useMemo(
    () => ({
      ...state,
      user,
      updateFromCookies,
      login,
      logout,
    }),
    [state, user, updateFromCookies],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function withAuthGuard<P>(
  Component: ComponentType<P>,
): ComponentType<P> {
  const Wrapper = (props: P) => {
    const { login, refreshToken } = useUserContext()
    const isRefreshValid = useMemo(
      () => refreshToken && !isTokenExpired(refreshToken),
      [refreshToken],
    )
    console.log('withAuthGuard isRefreshValid:', { isRefreshValid })

    useLayoutEffect(() => {
      if (!isRefreshValid) login()
    }, [isRefreshValid, login])

    if (!isRefreshValid) return null
    return <Component {...(props as P & { children?: unknown })} />
  }
  Wrapper.displayName = `withAuthGuard(${Component.displayName || 'Component'})`
  return Wrapper
}

/** Returns true if token is expired now. */
export const isTokenExpired = (token: string) =>
  isBefore(fromUnixTime(decode<{ exp: number }>(token).exp), new Date())

/** Retrieve auth status from server (httpOnly cookies). */
export const getAuthStatus = async (): Promise<Tokens> => {
  try {
    // Use the /api/auth/refresh endpoint for auth status check
    const response = await fetch('/api/auth/refresh', {
      method: 'GET',
      credentials: 'include',
    })

    if (response.ok) {
      const data = await response.json()

      if (data.hasTokens && data.user) {
        // We have valid tokens and user data
        return {
          token: data.token || 'server-managed',
          refreshToken: data.refreshToken || 'server-managed',
          user: data.user,
        }
      }
    } else {
      const data = await response.json().catch(() => ({}))

      if (data.error === 'TOKEN_EXPIRED' && data.needsRefresh) {
        // Token expired but we have refresh token - try to refresh
        console.log('🔄 Token expired, attempting refresh')
        const refreshResponse = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        })

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          if (refreshData.success && refreshData.user) {
            return {
              token: refreshData.token || 'server-managed',
              refreshToken: refreshData.refreshToken || 'server-managed',
              user: refreshData.user,
            }
          }
        }
      }
    }

    // No valid auth or refresh failed
    return { token: undefined, refreshToken: undefined }
  } catch (error) {
    console.error('Error getting auth status:', error)
    return { token: undefined, refreshToken: undefined }
  }
}

/** Legacy function for compatibility - now uses server check */
export const getTokensSync = (): Tokens => {
  // Can't read httpOnly cookies synchronously
  // This will be replaced by async auth status checks
  return { token: undefined, refreshToken: undefined }
}

export const logout = () => {
  // Server will handle clearing httpOnly cookies
  window.location.href = '/api/auth/logout'
}

/** Redirects to login */
export const login = () => {
  const redirect = `${location.pathname}${location.search}`
  window.location.href = `/login?redirect=${encodeURIComponent(redirect)}`
}
